var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/* Lit imports */
import { html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { updateWhenLocaleChanges } from '@lit/localize';
/* Other 3rd party imports */
import { v4 as uuidv4 } from 'uuid';
import DataGrid from 'devextreme/ui/data_grid';
import Tabs from 'devextreme/ui/tabs';
import { getLocale } from '../../i18n/localize';
let DinapsisTableSerieChart = class DinapsisTableSerieChart extends LitElement {
    /**
     * Initializes the component and sets the initial properties.
     */
    constructor() {
        super();
        /**
         * A property that is used to set the width
         */
        this.width = '100%';
        /**
         * A property that is used to set the height
         */
        this.height = '100%';
        /**
         * Determines if it shows the button to export the table.
         */
        this.export = false;
        /**
        * Property used to set the color of texts and icons when there isn't data.
        * Default value is #e64949.
        */
        this.colorNoData = '#e64949';
        /**
        * Property used to set an icon in the chart area when there isn't data.
        * Only accepts values from the Material iconographic library.
        * Default value is warning.
        */
        this.iconNoChartData = 'warning';
        /**
        * Property used to set a text in the chart area when there isn't data.
        * Default value is 'Gráfica sin datos'.
        */
        this.textNoTableData = 'Tabla sin datos';
        /**
         * Model that should display the table.
         */
        this.models = [];
        /**
         * DevExtreme tabs component
         */
        this.tabs = undefined;
        /**
         * A property that is used to generate unique idTabs
         */
        this.idTabs = `container-tabs-${uuidv4()}`;
        /**
         * A property that is used to generate unique idTableChart
         */
        this.idTableChart = `container-table-chart-${uuidv4()}`;
        /**
         * Indicates if all tabs are disabled because there aren´t data
         */
        this.areAllTabsDisabled = true;
        /**
         * Contains the title of the selected tab
         */
        this.selectedTabTitle = undefined;
        /**
         * Property that defines the title for the column that represents the x-axis
         */
        this.xAxisTitle = undefined;
        /**
         * Property that defines the title for the column that represents the y-axis
         */
        this.yAxisTitle = undefined;
        /**
         * Default date format if no format exists in the series
         */
        this.defaultDateFormat = 'dd/MM/yyyy';
        /**
         * Date format of the selected serie
         */
        this.serieXAxisDataFormat = undefined;
        /**
         * Default value for colum width
         */
        this.columnWidth = 200;
        updateWhenLocaleChanges(this);
    }
    /**
     * The function returns a string of HTML that contains a div that contains the component.
     * @returns The return statement is returning the html template literal
     */
    render() {
        return html `
      <div
        style="display:${this.hasSeries() ? 'block' : 'none'}"
        class="dinapsis-table-serie-chart"
      >
        <div id="${this.idTabs}" class="dinapsis-tab"></div>
        <div id="${this.idTableChart}" class="dinapsis-grid"></div>
      </div>
      <!-- Render div without series -->
      <div style="display:${this.hasSeries() ? 'none' : 'block'};">
        <div
          class="no-chart-data container-no-data"
          style="color: ${this.colorNoData}; height: ${this.height}; width: ${this.width};"
        >
          <span class="material-icons-round no-data-icon x">${this.iconNoChartData}</span>
          <span class="no-data-text no-data-text">${this.textNoTableData}</span>
        </div>
      </div>
    `;
    }
    /**
     * This method is from the Lit lifecycle and is used to subscribe to the event
     */
    connectedCallback() {
        super.connectedCallback();
        const self = this;
        this.addEventListener('dinapsis-chart-optionChanged', (event) => {
            self.models = event.detail;
            self.xAxisTitle = event.detail[0].xAxes[0].title;
            self.createSerieTabs();
        });
    }
    /**
     * If any property has changed when finish update this method is called an
     * depending on wich property has been modified,
     * it executes its update method
     * @param changedProperties - Map<string, unknown>
     */
    updated(changedProperties) {
        if (changedProperties.has('models')) {
            this.createSerieTabs();
        }
    }
    /**
     * The createRenderRoot function is a function that is called by the lit-html library to tell the
     * component where to render the html.
     * @returns The element itself.
     */
    createRenderRoot() {
        return this;
    }
    /**
     * Checks if models has any serie.
     */
    hasSeries() {
        return this.models[0].series.length > 0;
    }
    /**
     * Creates de component with all the tabs of the series.
     */
    createSerieTabs() {
        var _a, _b, _c;
        const container = document.getElementById(this.idTabs);
        if (!container)
            return;
        container.innerHTML = '';
        (_a = this.tabs) === null || _a === void 0 ? void 0 : _a.dispose();
        this.tabs = new Tabs(container);
        const items = [];
        // Sets the tab text and disables it if there is no data
        this.models[0].series.forEach((serie) => {
            items.push({
                text: serie.title,
                disabled: (!serie.data || serie.data.length < 1),
            });
        });
        // Sort tabs, placing disabled tabs on the right
        items.sort((a, b) => {
            const x = a.disabled ? 1 : 0;
            const y = b.disabled ? 1 : 0;
            return x < y ? -1 : 1;
        });
        this.areAllTabsDisabled = (_c = (_b = items[0]) === null || _b === void 0 ? void 0 : _b.disabled) !== null && _c !== void 0 ? _c : false;
        this.tabs.option('dataSource', items);
        // Set selected item handler
        this.tabs.option('onOptionChanged', (event) => {
            if (event.name !== 'selectedItem')
                return;
            this.selectedTabTitle = event.value.text;
            this.createSerieGrid();
        });
        this.tabs.option('selectedIndex', 0);
    }
    /**
     * Create the grid component in the table chart container
     */
    createSerieGrid() {
        const container = document.getElementById(this.idTableChart);
        if (this.areAllTabsDisabled) {
            container.innerHTML = this.createNoDataDiv();
            return;
        }
        this.dataGrid = new DataGrid(container);
        this.dataGrid.option('allowColumnResizing', true);
        this.dataGrid.option('showBorders', true);
        this.dataGrid.option('allowColumnReordering', true);
        this.dataGrid.option('keyExpr', 'category');
        this.dataGrid.option('export', { enabled: this.export });
        this.dataGrid.option('width', this.width);
        this.dataGrid.option('height', this.height);
        this.dataGrid.option('dataSource', this.getDataFromModels(this.selectedTabTitle));
        this.dataGrid.option('columns', this.setColumns());
        this.dataGrid.option('onContentReady', () => {
            // delete default grid free row space
            this.deleteGridFreeSpace(container);
            if (!this.export)
                return;
            const downloadIcon = container.querySelector(`#${this.idTableChart} .dx-icon-xlsxfile`);
            const parentDownloadIcon = downloadIcon === null || downloadIcon === void 0 ? void 0 : downloadIcon.parentElement;
            if (!downloadIcon)
                return;
            downloadIcon.remove();
            const newDownloadElement = this.createCustomElementDownloadXlsx();
            parentDownloadIcon === null || parentDownloadIcon === void 0 ? void 0 : parentDownloadIcon.appendChild(newDownloadElement);
        });
    }
    /**
     * Return a string with a div with the no data icon and text
     * @returns A string with the html of a DIV element
     */
    createNoDataDiv() {
        return `
      <div class="no-chart-data container-no-data x" style="color: ${this.colorNoData}; height: ${this.height}; width: ${this.width}">
        <span class="material-icons-round no-data-icon">${this.iconNoChartData}</span>
        <span class="no-data-text no-data-text">${this.textNoTableData}</span>
      </div>
    `;
    }
    /**
    * The function removes any elements with the class name 'dx-freespace-row' from
    * the given container.
    * @param {HTMLElement} container - The `container` parameter is an HTMLElement that represents the
    * container element in which the grid is located.
    */
    deleteGridFreeSpace(container) {
        const freeRowSpace = Array.from(container.getElementsByClassName('dx-freespace-row'));
        if (freeRowSpace.length > 0) {
            freeRowSpace.forEach((element) => {
                element.remove();
            });
        }
    }
    /**
     * This function creates a custom HTML element for downloading an XLSX file.
     * @returns A new HTML element is being returned.
     */
    createCustomElementDownloadXlsx() {
        const newDownloadElement = document.createElement('span');
        newDownloadElement.className = 'material-icons dinapsis-download-xlsxfile';
        newDownloadElement.innerHTML = 'download';
        return newDownloadElement;
    }
    /**
     * Converts the models object to a data source compatible object that is
     * accepted by the DevExtreme grid component
     * @returns A array of @see ConvertedData
     */
    getDataFromModels(selectedTabTitle) {
        const convertedData = [];
        const locale = getLocale() ? getLocale() : 'es';
        this.models.forEach((model) => {
            const config = {
                minimumFractionDigits: model.decimalNumber,
                maximumFractionDigits: model.decimalNumber,
            };
            model.series.forEach((serie) => {
                var _a, _b, _c, _d, _e;
                const unit = (_a = model.yAxes.find((axis) => axis.id === serie.yAxeId)) === null || _a === void 0 ? void 0 : _a.unitSymbol;
                if (!serie.data || serie.title !== selectedTabTitle)
                    return;
                this.xAxisTitle = (_b = model.xAxes.find((axis) => axis.id === serie.xAxeId)) === null || _b === void 0 ? void 0 : _b.title;
                this.yAxisTitle = (_c = model.yAxes.find((axis) => axis.id === serie.yAxeId)) === null || _c === void 0 ? void 0 : _c.title;
                this.yAxisTitle = unit !== '' ? `${this.yAxisTitle} (${unit})` : this.yAxisTitle;
                const categoryDataType = (_d = model.xAxes.find((axis) => axis.id === serie.xAxeId)) === null || _d === void 0 ? void 0 : _d.type;
                this.serieXAxisDataFormat = categoryDataType === 'Date'
                    ? this.getDateFormatFromTooltipFormat((_e = serie.options) === null || _e === void 0 ? void 0 : _e.tooltipFormat)
                    : '';
                serie.data.forEach((data) => {
                    if (data.category != null && data.value != null) {
                        let dataCategory = data.category;
                        if (typeof data.category === 'string' && categoryDataType === 'Date') {
                            dataCategory = new Date(data.category);
                        }
                        const item = {
                            category: dataCategory,
                            value: Number(data.value).toLocaleString(locale, config),
                        };
                        convertedData.push(item);
                    }
                });
            });
        });
        return convertedData;
    }
    /**
     * Set columns configuration of the grid
     * @returns Array of @See Columns
     */
    setColumns() {
        const columns = [
            {
                dataField: 'category',
                caption: this.xAxisTitle,
                fixed: false,
                format: this.serieXAxisDataFormat,
                width: this.columnWidth,
            },
            {
                dataField: 'value',
                caption: this.yAxisTitle,
                alignment: 'right',
                width: this.columnWidth,
            },
            {
                dataField: undefined,
            },
        ];
        return columns;
    }
    /**
     * Gets the date format of the serie
     * @param tooltipFormat TooltipFormat string from chart
     * @returns The string that contains the date format if it is defined in tooltipFormat
     */
    getDateFormatFromTooltipFormat(tooltipFormat) {
        if (!tooltipFormat)
            return this.defaultDateFormat;
        const formatDateIndexString = tooltipFormat.indexOf('formatDate(');
        if (formatDateIndexString === -1)
            return this.defaultDateFormat;
        const formatDateString = tooltipFormat.slice(formatDateIndexString);
        const formatDateIndex = formatDateString.indexOf('(') + 2;
        const formatDateIndexlast = formatDateString.indexOf(')}') - 1;
        return formatDateString.slice(formatDateIndex, formatDateIndexlast);
    }
};
__decorate([
    property({ type: String })
], DinapsisTableSerieChart.prototype, "width", void 0);
__decorate([
    property({ type: String })
], DinapsisTableSerieChart.prototype, "height", void 0);
__decorate([
    property({ type: Boolean })
], DinapsisTableSerieChart.prototype, "export", void 0);
__decorate([
    property({ type: String })
], DinapsisTableSerieChart.prototype, "colorNoData", void 0);
__decorate([
    property({ type: String })
], DinapsisTableSerieChart.prototype, "iconNoChartData", void 0);
__decorate([
    property({ type: String })
], DinapsisTableSerieChart.prototype, "textNoTableData", void 0);
__decorate([
    state(),
    property({ type: Array })
], DinapsisTableSerieChart.prototype, "models", void 0);
DinapsisTableSerieChart = __decorate([
    customElement('dinapsis-table-serie-chart')
], DinapsisTableSerieChart);
export { DinapsisTableSerieChart };
//# sourceMappingURL=dinapsis-table-serie-chart.js.map